import {
  Filter,
  FindAllParams,
  LoggerFactory,
  PageableResult,
  RemoteDataSourceOptions,
  RemoteListDataSource
} from "lego-gui-lib";
import { Lock } from "lego-gui-lib/lib/helpers/asyncUtils";
import { isNil, unionBy } from "lodash";
import { action } from "mobx";

export interface ContextAwareListDataSourceOptions
  extends RemoteDataSourceOptions {
  observableDataSource: string;
  observableField?: string;
  observableFieldRef: string;
}

export class ContextAwareListDataSource<T, ID> extends RemoteListDataSource<
  T,
  ID
> {
  private observableDataSource: string;
  private observableField: string;
  private observableFieldRef: string;
  private prevValue: string | undefined;
  private lock: Lock;

  public static clone<T, ID>(
    old: ContextAwareListDataSource<T, ID>,
    options?: Partial<ContextAwareListDataSourceOptions>
  ): ContextAwareListDataSource<T, ID> {
    options = options || {};

    const observableDataSource =
      options.observableDataSource || old.observableDataSource;

    if (!observableDataSource) {
      throw new Error("observable data source name should not be undefined");
    }

    return new ContextAwareListDataSource<T, ID>({
      api: options.api || old.api,
      name: options.name || old.name,
      schema: options.schema || old.schema,
      url: options.url || old.url,
      commons: options.commons || old.commons,
      params: options.params || old.params,
      observableField: options.observableField || old.observableField,
      observableFieldRef: options.observableFieldRef || old.observableFieldRef,
      observableDataSource: observableDataSource
    });
  }

  public constructor(options: ContextAwareListDataSourceOptions) {
    super(options);
    this.observableDataSource = options.observableDataSource;
    this.observableField = options.observableField || "id";
    this.observableFieldRef =
      options.observableFieldRef || this.observableField;
    this.insertObservableFieldToDefaultParams("-1");
    this.lock = new Lock(this.name, false);
    this.log = LoggerFactory.create(
      `ContextAwareListDataSource [${this.name}]`
    );
    this.commons.on(`${this.observableDataSource}.one.load`, (one: any) =>
      this.lock.lock()
    );
    this.commons.on(
      `${this.observableDataSource}.one.load.success`,
      (one: any) => this.observableChanged(one[0])
    );
    this.commons.on(`${this.observableDataSource}.one.clear`, () =>
      this.observableChanged(null)
    );
  }

  @action
  public async findAll(
    params?: FindAllParams,
    force?: boolean
  ): Promise<PageableResult<T>> {
    this.log.debug("findAll waiting for context");
    await this.lock.waitForUnlock();
    this.log.debug("executing findAll");
    return super.findAll(params, false);
  }

  @action
  public clear(): void {
    this.log.debug("clear context aware datasource");
    super.clear();
    this.prevValue = undefined;
  }

  @action
  public observableChanged(observable: any): void {
    observable = observable || {};

    this.log.debug("observable datasource changed", observable);

    let newValue = observable[this.observableField];
    if (newValue !== this.prevValue) {
      this.prevValue = newValue;
      newValue = isNil(newValue) ? "-1" : newValue;
      this.insertObservableFieldToDefaultParams(newValue);
      this.reload("observable field changed");
    } else {
      this.log.debug(
        "observable field is same as before",
        newValue,
        this.prevValue
      );
    }
    this.lock.unlock();
  }

  private insertObservableFieldToDefaultParams(newValue: string) {
    this.defaultParams = {
      ...this.defaultParams,
      filters: this.mergeFilters(newValue, this.defaultParams.filters)
    };
  }

  private mergeFilters(newValue: any, filters: Filter[]) {
    return unionBy(
      [{ field: this.observableFieldRef, value: newValue }],
      filters,
      "field"
    );
  }
}
